import { create } from "zustand";
import { RootState, INITIAL_STATE, Project } from "../core/state-shape";
import { http } from "../lib/http";
import { toast } from "sonner";
import useSyncStore from "./sync";
import { measure } from "@/lib/performance";
import { useUiStore } from "./ui";

const ACTIVE_PROJECT_ID_KEY = 'activeProjectId';

export type ProjectsState = RootState["projects"];

type ProjectsActions = {
  setState: (newState: ProjectsState) => void;
  patch: (p: Partial<ProjectsState>) => void;
  reset: () => void;
  loadProjects: () => Promise<void>;
  createProject: (projectData: Partial<Project>) => Promise<Project>;
  selectProject: (projectId: string | null) => Promise<void>;
  removeProject: (projectId: string) => Promise<void>;
  connectProvider: (
    provider: string,
    token: string
  ) => Promise<{ orgsAvailable: Project["orgsAvailable"]; provider: string }>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
};

console.log(localStorage.getItem(ACTIVE_PROJECT_ID_KEY))

export const useProjectsStore = create<ProjectsState & ProjectsActions>((set, get) => ({
  ...INITIAL_STATE.projects,
  activeProjectId: localStorage.getItem(ACTIVE_PROJECT_ID_KEY) || null,
  setState: (newState) => {
    set({...newState, activeProjectId: get().activeProjectId});
  },
  patch: (p) => set({...p, activeProjectId: get().activeProjectId}),
  reset: () => set({ ...INITIAL_STATE.projects, activeProjectId: get().activeProjectId }),

  loadProjects: measure(async () => {
    try {
      const projects = await http<Project[]>("/api/projects", { sendProjectId: false });
      const previousById = get().byId || {};
      const byId: Record<string, Project> = {};
      projects.forEach((p) => {
        const existing = previousById[p.id];
        const merged = {
          ...(existing ?? {}),
          ...p,
        } as Project;

        if (p.workspaceState === undefined && existing?.workspaceState !== undefined) {
          (merged as Project).workspaceState = existing.workspaceState;
        }

        byId[p.id] = merged;
      });
      set({ byId, list: projects.map((p) => p.id) });
    } catch (error) {
      console.error("Failed to load projects", error);
      toast.error("Failed to load projects.");
    }
  }, 'projects.loadProjects'),

  createProject: measure(async (projectData) => {
    const newProject = await http<Project>("/api/projects", {
      method: "POST",
      body: JSON.stringify(projectData),
      sendProjectId: false,
    });
    await useSyncStore.getState().fetchSnapshot(); // Refresh entire state
    toast.success(`Project "${newProject.name}" created.`);
    return newProject;
  }, 'projects.createProject'),

  selectProject: measure(async (projectId) => {
    console.log('SETTING ACTIVE PROJECT ID', projectId)
    if (!projectId) {
      localStorage.removeItem(ACTIVE_PROJECT_ID_KEY);
      set({ activeProjectId: null });
      useUiStore.getState().setIsFileExplorerSearchVisible(false);
      useUiStore.getState().setIsGitPanelVisible(false);
      return;
    }
    localStorage.setItem(ACTIVE_PROJECT_ID_KEY, projectId);
    set({ activeProjectId: projectId });
    useUiStore.getState().setIsFileExplorerSearchVisible(false);
    useUiStore.getState().setIsGitPanelVisible(false);
    await useSyncStore.getState().fetchSnapshot(); // The backend has now switched context
  }, 'projects.selectProject'),

  removeProject: measure(async (projectId) => {
    await http(`/api/projects/${projectId}`, {
      method: "DELETE",
      sendProjectId: false,
    });
    await useSyncStore.getState().fetchSnapshot();
    toast.success("Project removed.");
  }, 'projects.removeProject'),

  connectProvider: measure(async (provider, token) => {
    const res = await http<{ orgsAvailable: Project["orgsAvailable"] }>("/api/projects/connect_provider", {
      method: "POST",
      body: JSON.stringify({ provider, token }),
      sendProjectId: false,
    });
    return { orgsAvailable: res.orgsAvailable, provider };
  }, 'projects.connectProvider'),
  updateProject: measure(async (projectId, updates) => {
    try {
      const response = await http<Project>(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
        sendProjectId: false,
      });
      set((state) => {
        const current = state.byId?.[projectId];
        const next = { ...current, ...response, ...updates } as Project;

        return {
          byId: {
            ...state.byId,
            [projectId]: next,
          },
        };
      });
    } catch (e: any) {
      toast.error('Failed to update project.', { description: e.message });
    }
  }, 'projects.updateProject'),
}));